/**
 * ErpVisitesSecuritePanel — suivi des visites de la commission de sécurité
 * (item 4.5).
 *
 * ─── CE QUE CET ÉCRAN SERT À FAIRE ──────────────────────────────────────────
 *
 * Répondre à : **quel bâtiment doit être visité, et lequel a un problème ?**
 *
 * Il ouvre donc sur les échéances les plus proches et met en évidence les avis
 * défavorables — un ERP sous avis défavorable engage directement la
 * responsabilité du maire.
 *
 * ─── CE QUE L'ÉCRAN EXPLIQUE, ET POURQUOI ───────────────────────────────────
 *
 * Deux règles surprennent si on ne les explique pas :
 *
 *   · une salle des fêtes de 5e catégorie n'affiche AUCUNE échéance — c'est
 *     normal depuis le décret 2025-1100, et l'écran le dit plutôt que de
 *     laisser croire à un oubli ;
 *   · la date de prochaine visite peut passer à 5 ans d'un coup — c'est la
 *     prolongation de l'article GE 4 §3, et un bandeau l'annonce au moment où
 *     elle joue. Une date qui change sans explication inquiète à juste titre.
 */

import { useEffect, useState, useCallback } from 'react';
import { Card, Badge, Alert, Button } from '@/components/ui';
import { api, ApiException } from '@/lib/api';
import type { ErpASuivre, ErpVisiteSecurite, ErpAvisVisite } from '@roronoa-collectivites/shared-types';
import { ShieldCheck, Plus, Info, CalendarClock } from 'lucide-react';

const AVIS: Array<{ value: ErpAvisVisite; label: string }> = [
  { value: 'favorable', label: 'Favorable' },
  { value: 'favorable_avec_prescriptions', label: 'Favorable avec prescriptions' },
  { value: 'defavorable', label: 'Défavorable' },
];

const fr = (iso: string | null) => (iso ? iso.split('-').reverse().join('/') : '—');

function joursRestants(iso: string | null): number | null {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  const cible = new Date(y!, (m ?? 1) - 1, d ?? 1);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((cible.getTime() - today.getTime()) / 86_400_000);
}

function EcheanceBadge({ e }: { e: ErpASuivre }) {
  if (e.periodiciteMois === null) {
    return (
      <Badge variant="neutral" title="ERP de 5e catégorie sans locaux à sommeil : non soumis à visite périodique (décret 2025-1100)">
        Non soumis
      </Badge>
    );
  }
  const j = joursRestants(e.visiteProchaine);
  if (j === null) return <Badge variant="warning">À planifier</Badge>;
  if (j < 0) return <Badge variant="urgent">En retard de {Math.abs(j)} j</Badge>;
  if (j <= 90) return <Badge variant="warning">Dans {j} j</Badge>;
  return <Badge variant="success">Dans {Math.round(j / 30)} mois</Badge>;
}

function AvisBadge({ avis }: { avis: ErpAvisVisite | null }) {
  if (!avis) return <span className="text-ink-subtle">—</span>;
  if (avis === 'defavorable') return <Badge variant="urgent">Défavorable</Badge>;
  if (avis === 'favorable_avec_prescriptions') return <Badge variant="warning">Avec prescriptions</Badge>;
  return <Badge variant="success">Favorable</Badge>;
}

export function ErpVisitesSecuritePanel() {
  const [parc, setParc] = useState<ErpASuivre[]>([]);
  const [historique, setHistorique] = useState<ErpVisiteSecurite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formLieu, setFormLieu] = useState<ErpASuivre | null>(null);

  const [dateVisite, setDateVisite] = useState('');
  const [avis, setAvis] = useState<ErpAvisVisite>('favorable');
  const [prescriptions, setPrescriptions] = useState('');
  const [referencePv, setReferencePv] = useState('');

  const charger = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [p, h] = await Promise.all([api.admin.lieux.erpASuivre(), api.admin.lieux.visitesSecurite()]);
      setParc(p); setHistorique(h);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Chargement impossible');
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { void charger(); }, [charger]);

  async function enregistrer() {
    if (!formLieu || !dateVisite) { setError('Renseignez la date de visite'); return; }
    setSaving(true); setError(null);
    try {
      const r = await api.admin.lieux.enregistrerVisiteSecurite({
        lieuId: formLieu.id, dateVisite, avis,
        prescriptions: prescriptions || null,
        referencePv: referencePv || null,
      });
      setMsg(
        r.prolongee
          ? `Visite enregistrée. Prochaine visite portée au ${fr(r.prochaine)} : deux avis favorables consécutifs permettent la prolongation à 5 ans (article GE 4 §3).`
          : r.prochaine
            ? `Visite enregistrée. Prochaine visite attendue le ${fr(r.prochaine)}.`
            : 'Visite enregistrée. Ce bâtiment n\u2019est pas soumis à visite périodique.',
      );
      setFormLieu(null); setDateVisite(''); setPrescriptions(''); setReferencePv('');
      setAvis('favorable');
      await charger();
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Enregistrement impossible');
    } finally { setSaving(false); }
  }

  async function changerPeriodicite(lieu: ErpASuivre, valeur: string) {
    const mois = valeur === '' ? null : Number(valeur);
    try {
      await api.admin.lieux.reglerPeriodiciteVisite(lieu.id, mois);
      await charger();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }

  const champ = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink';
  const label = 'mb-1 block text-2xs uppercase tracking-r-eyebrow text-ink-subtle';

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded>
        <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
          <ShieldCheck className="h-4 w-4 text-tech" aria-hidden="true" />
          Visites de sécurité ERP
        </h3>

        <p className="mb-4 flex items-start gap-1.5 text-xs text-ink-muted">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            La périodicité est proposée d'après la catégorie et les locaux à sommeil, puis
            modifiable — le tableau de l'arrêté du 25 juin 1980 peut être adapté par arrêté
            du maire. Les ERP de 5<sup>e</sup> catégorie sans hébergement ne sont plus soumis
            à visite périodique (décret 2025-1100).
          </span>
        </p>

        {loading ? (
          <p className="text-sm text-ink-subtle">Chargement…</p>
        ) : parc.length === 0 ? (
          <p className="text-sm text-ink-subtle">
            Aucun bâtiment classé ERP. Renseignez la catégorie dans la fiche du lieu.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                  <th className="py-2 font-medium">Bâtiment</th>
                  <th className="py-2 font-medium">Classement</th>
                  <th className="py-2 font-medium">Périodicité</th>
                  <th className="py-2 font-medium">Dernière visite</th>
                  <th className="py-2 font-medium">Avis</th>
                  <th className="py-2 font-medium">Prochaine</th>
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody>
                {parc.map((e) => (
                  <tr key={e.id} className="border-b border-border/60 last:border-0">
                    <td className="py-2 text-ink">
                      {e.nom}
                      {e.locauxSommeil && (
                        <span className="ml-1.5 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                          hébergement
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-ink-muted">
                      {e.typeErp ? `${e.typeErp} · ` : ''}{e.categorieErp}<sup>e</sup> cat.
                    </td>
                    <td className="py-2">
                      <select
                        value={e.periodiciteMois ?? ''}
                        onChange={(ev) => void changerPeriodicite(e, ev.target.value)}
                        className="h-8 rounded-r-md border border-border bg-surface/60 px-1.5 text-xs text-ink"
                        aria-label={`Périodicité pour ${e.nom}`}
                      >
                        <option value="">Non soumis</option>
                        <option value="24">2 ans</option>
                        <option value="36">3 ans</option>
                        <option value="60">5 ans</option>
                      </select>
                    </td>
                    <td className="py-2 tabular-nums text-ink-muted">{fr(e.derniereVisite)}</td>
                    <td className="py-2"><AvisBadge avis={e.dernierAvis} /></td>
                    <td className="py-2"><EcheanceBadge e={e} /></td>
                    <td className="py-2 text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setFormLieu(e); setMsg(null); }}>
                        <Plus className="h-4 w-4" aria-hidden="true" /> Visite
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {formLieu && (
        <Card variant="glass" padded>
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
            <CalendarClock className="h-4 w-4 text-tech" aria-hidden="true" />
            Nouvelle visite — {formLieu.nom}
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className={label}>Date de la visite *</label>
              <input type="date" value={dateVisite} onChange={(e) => setDateVisite(e.target.value)} className={champ} />
            </div>
            <div>
              <label className={label}>Avis de la commission</label>
              <select value={avis} onChange={(e) => setAvis(e.target.value as ErpAvisVisite)} className={champ}>
                {AVIS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
              </select>
            </div>
            <div>
              <label className={label}>Référence du procès-verbal</label>
              <input value={referencePv} onChange={(e) => setReferencePv(e.target.value)}
                className={champ} placeholder="PV-2026-0042" maxLength={120} />
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Prescriptions et observations</label>
              <textarea value={prescriptions} onChange={(e) => setPrescriptions(e.target.value)}
                rows={3} maxLength={4000}
                className="w-full rounded-r-md border border-border bg-surface/60 p-2 text-sm text-ink"
                placeholder="Travaux demandés par la commission…" />
            </div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <Button variant="ghost" size="sm" onClick={() => setFormLieu(null)}>Annuler</Button>
              <Button variant="primary" size="sm" loading={saving} onClick={() => void enregistrer()}>
                Enregistrer la visite
              </Button>
            </div>
          </div>
        </Card>
      )}

      {historique.length > 0 && (
        <Card variant="glass" padded>
          <p className="mb-2 text-sm font-semibold text-ink">Historique des visites</p>
          <ul className="space-y-2 text-sm">
            {historique.slice(0, 20).map((v) => (
              <li key={v.id} className="flex flex-wrap items-baseline gap-2 border-b border-border/40 pb-2 last:border-0">
                <span className="tabular-nums text-ink-muted">{fr(v.dateVisite)}</span>
                <span className="font-medium text-ink">{v.lieuNom}</span>
                <AvisBadge avis={v.avis} />
                {v.referencePv && <span className="text-xs text-ink-subtle">{v.referencePv}</span>}
                {v.prescriptions && (
                  <span className="w-full text-xs text-ink-muted">{v.prescriptions}</span>
                )}
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
